import React, { useEffect, useState } from 'react';
import { Form, Alert, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { current, updateProfil } from '../Redux/Actions/AuthActions';
import { loadClient, updateClientProfile } from '../Redux/Actions/ClientActions';
import { ArrowLeft, User, Mail, Lock, CheckCircle2 } from 'lucide-react';
import './UpdateUser.css';
import '../pages/ScanPoints.css'; // For mobile-app-container and header styles

const UpdateProfil = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get both states
  const { user: adminUser } = useSelector((state) => state.AuthReducer) || {};
  const { client: clientUser } = useSelector((state) => state.clientReducer) || {};

  // Determine user type
  const isClient = !!localStorage.getItem('clientToken');
  const user = isClient ? clientUser : adminUser;

  useEffect(() => {
    if (isClient) {
      if (!clientUser) dispatch(loadClient());
    } else {
      if (!adminUser) dispatch(current());
    }
  }, [dispatch, isClient, clientUser, adminUser]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (password && password !== confirmPassword) {
      setErrorMessage('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    if (!oldPassword) {
      setErrorMessage("L'ancien mot de passe est obligatoire.");
      return;
    }

    setLoading(true);
    const updatedData = { name, email, oldPassword };
    if (password) {
      updatedData.password = password;
    }

    try {
      if (isClient) {
        const res = await dispatch(updateClientProfile(updatedData));
        if (res.success) {
          setSuccessMessage('Profil client mis à jour avec succès !');
        } else {
          setErrorMessage(res.message);
        }
      } else {
        await dispatch(updateProfil(user._id, updatedData));
        setSuccessMessage('Profil admin mis à jour avec succès !');
      }
    } catch (err) {
      setErrorMessage("Une erreur est survenue lors de la mise à jour.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner animation="grow" variant="primary" />
      </div>
    );
  }

  return (
    <div className="mobile-app-container" style={{ background: '#f8f9fe', minHeight: '100vh' }}>
      {/* Header Mobile Natif */}
      <header style={{
        position: 'sticky',
        top: 0,
        padding: '1.25rem 1.5rem',
        background: 'white',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            backgroundColor: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            color: '#1e293b',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b', margin: 0 }}>
          {isClient ? 'Mon Profil Client' : 'Mon Profil Admin'}
        </h1>
      </header>

      <main className="mobile-main-feed animate-fade-in" style={{ padding: '1.5rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: isClient ? 'linear-gradient(135deg, #6366f1, #818cf8)' : 'linear-gradient(135deg, #f59e0b, #fbbf24)',
            borderRadius: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            color: 'white',
            fontWeight: '800',
            margin: '0 auto 1rem',
            boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
          }}>
            {(user.name?.[0] || 'U').toUpperCase()}
          </div>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>{user.email}</p>
        </div>

        {errorMessage && (
          <Alert variant="danger" style={{ borderRadius: '16px', border: 'none', background: '#fee2e2', color: '#991b1b', fontSize: '0.85rem' }}>
            {errorMessage}
          </Alert>
        )}

        {successMessage && (
          <Alert variant="success" className="flex items-center gap-2" style={{ borderRadius: '16px', border: 'none', background: '#ecfdf5', color: '#065f46', fontSize: '0.85rem' }}>
            <CheckCircle2 size={16} /> {successMessage}
          </Alert>
        )}

        <Form onSubmit={handleUpdate} className="space-y-6">
          <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
            Informations Générales
          </div>

          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', fontSize: '0.85rem', color: '#475569', marginLeft: '4px' }}>Nom Complet</Form.Label>
            <div style={{ position: 'relative' }}>
              <User size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <Form.Control
                style={{ height: '54px', borderRadius: '16px', paddingLeft: '48px', border: '1px solid #e2e8f0', background: 'white' }}
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
                placeholder="Votre nom"
                required
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', fontSize: '0.85rem', color: '#475569', marginLeft: '4px' }}>Adresse Email</Form.Label>
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <Form.Control
                style={{ height: '54px', borderRadius: '16px', paddingLeft: '48px', border: '1px solid #e2e8f0', background: 'white' }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="votre@email.com"
                required
                disabled={isClient} // Usually email is not changeable for security or requires extra steps
              />
            </div>
            {isClient && <p style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '4px', marginLeft: '4px' }}>L'email ne peut pas être modifié.</p>}
          </Form.Group>

          <div style={{ fontSize: '0.8rem', fontWeight: '800', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '2rem', marginBottom: '1rem' }}>
            Sécurité du compte
          </div>

          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', fontSize: '0.85rem', color: '#475569', marginLeft: '4px' }}>Ancien mot de passe</Form.Label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <Form.Control
                style={{ height: '54px', borderRadius: '16px', paddingLeft: '48px', border: '1px solid #e2e8f0', background: 'white' }}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                type="password"
                placeholder="Obligatoire pour valider"
                required
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', fontSize: '0.85rem', color: '#475569', marginLeft: '4px' }}>Nouveau mot de passe</Form.Label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <Form.Control
                style={{ height: '54px', borderRadius: '16px', paddingLeft: '48px', border: '1px solid #e2e8f0', background: 'white' }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                placeholder="Laisser vide pour ne pas changer"
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', fontSize: '0.85rem', color: '#475569', marginLeft: '4px' }}>Confirmer le nouveau</Form.Label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <Form.Control
                style={{ height: '54px', borderRadius: '16px', paddingLeft: '48px', border: '1px solid #e2e8f0', background: 'white' }}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                type="password"
                placeholder="Répétez le nouveau"
              />
            </div>
          </Form.Group>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              height: '58px',
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: 'white',
              border: 'none',
              borderRadius: '18px',
              fontWeight: '700',
              fontSize: '1.1rem',
              marginTop: '1rem',
              boxShadow: '0 10px 20px rgba(99, 102, 241, 0.2)',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? <Spinner size="sm" /> : 'Sauvegarder les modifications'}
          </button>
        </Form>
        <div className="pb-10"></div>
      </main>
    </div>
  );
};

export default UpdateProfil;
